import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CreditCard, Loader2, Pencil, Plus, RefreshCw, Star } from 'lucide-react';
import { toast } from 'sonner';
import {
  audienceLabel,
  fetchMembershipCatalog,
  nextTierFeatureState,
  setTierFeatureState,
  tierFeatureKey,
  tierReadinessIssue,
  type MembershipCatalog,
  type MembershipFeatureRow,
  type MembershipTierRow,
  type TierFeatureState,
} from '../../lib/membershipTiers';
import MembershipFeatureModal from './MembershipFeatureModal';
import MembershipTierModal from './MembershipTierModal';

const EMPTY_CATALOG: MembershipCatalog = { tiers: [], features: [], states: {} };

function priceSummary(tier: MembershipTierRow): string {
  if (tier.customAmountMin !== null) {
    return `From $${tier.customAmountMin}${tier.customAmountSuffix ?? ''}`.trim();
  }
  return tier.priceLabel || '—';
}

/** Both modes are always listed, so a blank live row reads as a gap rather than absence. */
function PriceId({ mode, value }: { mode: 'Live' | 'Test'; value: string | null }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="w-8 shrink-0 text-[10px] uppercase tracking-wide text-gray-400">{mode}</span>
      {value ? (
        <span className="font-mono text-xs break-all">{value}</span>
      ) : (
        <span className="inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          Missing
        </span>
      )}
    </div>
  );
}

export default function MembershipTiersView() {
  const [catalog, setCatalog] = useState<MembershipCatalog>(EMPTY_CATALOG);
  const [loading, setLoading] = useState(true);
  const [tierModalOpen, setTierModalOpen] = useState(false);
  const [editingTier, setEditingTier] = useState<MembershipTierRow | null>(null);
  const [featureModalOpen, setFeatureModalOpen] = useState(false);
  const [editingFeature, setEditingFeature] = useState<MembershipFeatureRow | null>(null);
  /** Cell being written, so the matrix can't be double-clicked into a race. */
  const [pendingCell, setPendingCell] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCatalog(await fetchMembershipCatalog());
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to load membership tiers');
      setCatalog(EMPTY_CATALOG);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const stateFor = (tierId: string, featureId: string): TierFeatureState =>
    catalog.states[tierFeatureKey(tierId, featureId)] ?? 'hidden';

  async function cycleCell(tier: MembershipTierRow, feature: MembershipFeatureRow) {
    const key = tierFeatureKey(tier.id, feature.id);
    if (pendingCell) return;
    const next = nextTierFeatureState(stateFor(tier.id, feature.id));

    setPendingCell(key);
    // Optimistic: the grid is the whole point of this panel, so it should not wait on
    // a round trip per click. Reverted from the server copy if the write fails.
    setCatalog((prev) => ({ ...prev, states: { ...prev.states, [key]: next } }));
    try {
      await setTierFeatureState(tier.id, feature.id, next);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Could not update benefit');
      await load();
    } finally {
      setPendingCell(null);
    }
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <CreditCard className="h-7 w-7 text-emerald-500" />
            Membership tiers
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Paid tiers for CTO as a Service, shown on members.stephansmith.me/upgrade. This is the
            catalog only — who is on a tier arrives with Stripe.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            title="Refresh"
            aria-label="Refresh"
            className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={() => {
              setEditingTier(null);
              setTierModalOpen(true);
            }}
            className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
          >
            <Plus className="w-4 h-4" />
            New tier
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/40 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Tier</th>
                    <th className="text-left px-4 py-3 font-medium">Audience</th>
                    <th className="text-left px-4 py-3 font-medium">Price</th>
                    <th className="text-left px-4 py-3 font-medium">Stripe price</th>
                    <th className="text-left px-4 py-3 font-medium">Status</th>
                    <th className="text-right px-4 py-3 font-medium w-24">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {catalog.tiers.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-10 text-center text-gray-500 dark:text-gray-400"
                      >
                        No tiers yet.
                      </td>
                    </tr>
                  ) : (
                    catalog.tiers.map((tier) => {
                      const issue = tierReadinessIssue(tier);
                      return (
                        <tr key={tier.id} className="text-gray-800 dark:text-gray-200">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5 font-medium">
                              {tier.name}
                              {tier.featured ? (
                                <Star
                                  className="w-3.5 h-3.5 text-amber-500 shrink-0"
                                  aria-label="Featured"
                                />
                              ) : null}
                            </div>
                            <div className="font-mono text-xs text-gray-500 dark:text-gray-400">
                              {tier.slug}
                            </div>
                          </td>
                          <td className="px-4 py-3">{audienceLabel(tier.audience)}</td>
                          <td className="px-4 py-3">
                            <div>{priceSummary(tier)}</div>
                            {tier.priceHint ? (
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {tier.priceHint}
                              </div>
                            ) : null}
                            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              {tier.monthlyCallLimit == null
                                ? 'Unlimited calls/mo'
                                : `${tier.monthlyCallLimit.toLocaleString()} calls/mo`}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {tier.isFree ? (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                Not applicable
                              </span>
                            ) : (
                              <div className="space-y-0.5">
                                <PriceId mode="Live" value={tier.stripePriceId} />
                                <PriceId mode="Test" value={tier.stripePriceIdTest} />
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span
                                className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                  tier.active
                                    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200'
                                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                                }`}
                              >
                                {tier.active ? 'Active' : 'Inactive'}
                              </span>
                              {tier.isFree ? (
                                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-200">
                                  Free
                                </span>
                              ) : null}
                              {issue ? (
                                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                                  {issue}
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              onClick={() => {
                                setEditingTier(tier);
                                setTierModalOpen(true);
                              }}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                              Edit
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mt-10 mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                What each tier includes
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Click a cell to cycle it: blank (not listed) → included → struck through. The free
                card needs struck-through rows to show what an upgrade buys.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setEditingFeature(null);
                setFeatureModalOpen(true);
              }}
              className="inline-flex shrink-0 items-center gap-1 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <Plus className="w-4 h-4" />
              New benefit
            </button>
          </div>

          <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/40 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Benefit</th>
                    {catalog.tiers.map((tier) => (
                      <th key={tier.id} className="px-3 py-3 font-medium text-center">
                        {tier.name}
                      </th>
                    ))}
                    <th className="text-right px-4 py-3 font-medium w-24">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {catalog.features.length === 0 ? (
                    <tr>
                      <td
                        colSpan={catalog.tiers.length + 2}
                        className="px-4 py-10 text-center text-gray-500 dark:text-gray-400"
                      >
                        No benefits yet.
                      </td>
                    </tr>
                  ) : (
                    catalog.features.map((feature) => (
                      <tr key={feature.id} className="text-gray-800 dark:text-gray-200">
                        <td className="px-4 py-3">
                          <div className={feature.active ? '' : 'text-gray-400 dark:text-gray-500'}>
                            {feature.label}
                            {feature.active ? '' : ' (inactive)'}
                          </div>
                          <div className="font-mono text-xs text-gray-500 dark:text-gray-400">
                            {feature.slug}
                          </div>
                        </td>
                        {catalog.tiers.map((tier) => {
                          const key = tierFeatureKey(tier.id, feature.id);
                          const state = stateFor(tier.id, feature.id);
                          return (
                            <td key={tier.id} className="px-3 py-3 text-center">
                              <button
                                type="button"
                                onClick={() => void cycleCell(tier, feature)}
                                disabled={pendingCell !== null}
                                aria-label={`${feature.label} on ${tier.name}: ${state}`}
                                title={`${state} — click to change`}
                                className={`inline-flex h-7 w-7 items-center justify-center rounded-md border text-sm font-semibold transition-colors disabled:opacity-50 ${
                                  state === 'included'
                                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200'
                                    : state === 'excluded'
                                      ? 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:border-rose-800 dark:bg-rose-900/30 dark:text-rose-200'
                                      : 'border-gray-200 text-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-600 dark:hover:bg-gray-700'
                                }`}
                              >
                                {pendingCell === key ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : state === 'included' ? (
                                  '✓'
                                ) : state === 'excluded' ? (
                                  '×'
                                ) : (
                                  '–'
                                )}
                              </button>
                            </td>
                          );
                        })}
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingFeature(feature);
                              setFeatureModalOpen(true);
                            }}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <MembershipTierModal
        isOpen={tierModalOpen}
        onClose={() => setTierModalOpen(false)}
        tier={editingTier}
        onSuccess={() => void load()}
      />
      <MembershipFeatureModal
        isOpen={featureModalOpen}
        onClose={() => setFeatureModalOpen(false)}
        feature={editingFeature}
        onSuccess={() => void load()}
      />
    </div>
  );
}
